/**
 * Navigation utility functions for environment-aware routing
 * Handles path generation and navigation based on environment configuration
 */

/**
 * Get the base path from environment variables
 * @returns {string} The base path for the current environment
 */
export const getBasePath = () => {
  return process.env.REACT_APP_HOMEPAGE || '';
};

/**
 * Get the admin URL from environment variables
 * @returns {string} The admin URL for the current environment
 */
export const getAdminUrl = () => {
  return process.env.REACT_APP_ADMIN || 'https://app.phybercare.com/';
};

/**
 * Navigate to a specific path using environment-aware routing
 * @param {string} path - The path to navigate to (e.g., '/dashboard', '/profile')
 */
export const navigateTo = (path) => {
  if (typeof window !== "undefined") {
    const basePath = getBasePath();
    // Handle case where basePath is '/' to avoid double slashes
    const fullPath = basePath === '/' ? path : `${basePath}${path}`;
    window.location.href = fullPath;
  }
};

/**
 * Navigate to admin application
 */
export const navigateToAdmin = () => {
  if (typeof window !== "undefined") {
    const adminUrl = getAdminUrl();
    window.location.href = adminUrl;
  }
};

/**
 * Get full path for a given route (useful for links, redirects, etc.)
 * @param {string} path - The path to get full URL for
 * @returns {string} The full path including base path
 */
export const getFullPath = (path) => {
  const basePath = getBasePath();
  // Handle case where basePath is '/' to avoid double slashes
  return basePath === '/' ? path : `${basePath}${path}`;
};

/**
 * Check if we're in development environment
 * @returns {boolean} True if in development mode
 */
export const isDevelopment = () => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Get the home route based on environment
 * In development: redirects to /practitioner
 * In production: stays at /
 * @returns {string} The appropriate home route
 */
export const getHomeRoute = () => {
  return isDevelopment() ? getFullPath('/dashboard') : '/dashboard';
};

/**
 * Generate href attribute with environment-aware path
 * Use this for <a href={getHref('/dashboard')}>
 * @param {string} path - The path to generate href for
 * @returns {string} The full path for href attribute
 */
export const getHref = (path) => {
  return getFullPath(path);
};

/**
 * Get path segments from current URL, accounting for basepath
 * Use this instead of window.location.pathname.split("/")
 * @returns {string[]} Array of path segments without basepath
 */
export const getPathSegments = () => {
  if (typeof window === "undefined") return [];
  
  const pathname = window.location.pathname;
  const basePath = getBasePath();
  
  // Remove basepath from pathname if it exists
  let relativePath = pathname;
  if (basePath && basePath !== '/' && pathname.startsWith(basePath)) {
    relativePath = pathname.substring(basePath.length);
  }
  
  // Ensure path starts with /
  if (!relativePath.startsWith('/')) {
    relativePath = '/' + relativePath;
  }
  
  return relativePath.split('/');
};

/**
 * Get a specific path segment by index (accounting for basepath)
 * Use this instead of window.location.pathname.split("/")[index]
 * @param {number} index - The segment index (same as you'd use with split)
 * @returns {string} The path segment at the given index
 */
export const getPathSegment = (index) => {
  const segments = getPathSegments();
  return segments[index] || '';
};